import { buildReviewSeenKey } from './buildReviewSeenDocId';

/**
 * @typedef {'beer' | 'brewery'} ReviewDetailsItemType
 */

/**
 * @typedef {object} ReviewDetailsInboxItem
 * @property {ReviewDetailsItemType} itemType
 * @property {string} itemId
 * @property {string} name
 * @property {number | null} averageRating
 * @property {number} totalReviews
 * @property {number} checkinsCount
 * @property {number} unseenCount
 * @property {number} totalCount
 * @property {Date} latestReviewAt
 * @property {Date | null} latestUnseenAt
 */

/**
 * @param {Date | null} left
 * @param {Date | null} right
 * @returns {Date | null}
 */
function maxDate(left, right) {
  if (!left) {
    return right;
  }
  if (!right) {
    return left;
  }
  return left.getTime() >= right.getTime() ? left : right;
}

/**
 * @param {object} params
 * @param {string} params.breweryId
 * @param {string} params.breweryName
 * @param {number | null} params.breweryHospitalityRating
 * @param {object[]} params.beerCatalog
 * @param {Array<{ reviewType: 'beer', reviewId: string, beerId: string, date: Date }>} params.beerReviews
 * @param {Array<{ reviewType: 'hospitality', reviewId: string, date: Date }>} params.hospitalityReviews
 * @param {Set<string>} params.seenKeys
 * @returns {ReviewDetailsInboxItem[]}
 */
export function aggregateReviewInboxItems({
  breweryId,
  breweryName,
  breweryHospitalityRating,
  beerCatalog,
  beerReviews,
  hospitalityReviews,
  seenKeys,
}) {
  /** @type {Map<string, ReviewDetailsInboxItem>} */
  const beerItems = new Map();
  const beerById = new Map(
    beerCatalog.map((beer) => [typeof beer.id === 'string' ? beer.id : '', beer]),
  );

  for (const review of beerReviews) {
    const beerId = review.beerId;
    if (!beerId) {
      continue;
    }
    const seenKey = buildReviewSeenKey('beer', review.reviewId);
    const isUnseen = !seenKeys.has(seenKey);
    const beer = beerById.get(beerId);
    const existing = beerItems.get(beerId) ?? {
      itemType: 'beer',
      itemId: beerId,
      name: typeof beer?.name === 'string' && beer.name.length > 0 ? beer.name : 'Unknown Beer',
      averageRating: typeof beer?.ratings?.overall === 'number' ? beer.ratings.overall : null,
      totalReviews: typeof beer?.totalReviews === 'number' ? beer.totalReviews : 0,
      checkinsCount: typeof beer?.checkinsCount === 'number' ? beer.checkinsCount : 0,
      unseenCount: 0,
      totalCount: 0,
      latestReviewAt: review.date,
      latestUnseenAt: null,
    };

    existing.totalCount += 1;
    existing.latestReviewAt = maxDate(existing.latestReviewAt, review.date) ?? review.date;
    if (isUnseen) {
      existing.unseenCount += 1;
      existing.latestUnseenAt = maxDate(existing.latestUnseenAt, review.date) ?? review.date;
    }
    beerItems.set(beerId, existing);
  }

  /** @type {ReviewDetailsInboxItem | null} */
  let breweryItem = null;
  for (const review of hospitalityReviews) {
    const seenKey = buildReviewSeenKey('hospitality', review.reviewId);
    const isUnseen = !seenKeys.has(seenKey);
    if (!breweryItem) {
      breweryItem = {
        itemType: 'brewery',
        itemId: breweryId,
        name: breweryName,
        averageRating: breweryHospitalityRating,
        totalReviews: 0,
        checkinsCount: 0,
        unseenCount: 0,
        totalCount: 0,
        latestReviewAt: review.date,
        latestUnseenAt: null,
      };
    }
    breweryItem.totalCount += 1;
    breweryItem.latestReviewAt = maxDate(breweryItem.latestReviewAt, review.date) ?? review.date;
    if (isUnseen) {
      breweryItem.unseenCount += 1;
      breweryItem.latestUnseenAt = maxDate(breweryItem.latestUnseenAt, review.date) ?? review.date;
    }
  }

  const items = [...beerItems.values()];
  if (breweryItem) {
    items.push(breweryItem);
  }

  return items;
}

/**
 * @param {ReviewDetailsInboxItem} item
 * @returns {string}
 */
export function buildReviewInboxItemKey(item) {
  return `${item.itemType}:${item.itemId}`;
}

/**
 * @param {ReviewDetailsInboxItem} leftItem
 * @param {ReviewDetailsInboxItem} rightItem
 * @param {'new-reviews' | 'all-reviews'} tab
 * @returns {number}
 */
function compareReviewInboxItems(leftItem, rightItem, tab) {
  const leftSortDate = tab === 'new-reviews'
    ? (leftItem.latestUnseenAt ?? leftItem.latestReviewAt)
    : leftItem.latestReviewAt;
  const rightSortDate = tab === 'new-reviews'
    ? (rightItem.latestUnseenAt ?? rightItem.latestReviewAt)
    : rightItem.latestReviewAt;
  const dateDiff = rightSortDate.getTime() - leftSortDate.getTime();
  if (dateDiff !== 0) {
    return dateDiff;
  }
  const typeDiff = leftItem.itemType.localeCompare(rightItem.itemType);
  if (typeDiff !== 0) {
    return typeDiff;
  }
  return leftItem.itemId.localeCompare(rightItem.itemId);
}

/**
 * @param {ReviewDetailsInboxItem[]} items
 * @param {'new-reviews' | 'all-reviews'} tab
 * @returns {ReviewDetailsInboxItem[]}
 */
export function sortReviewInboxItems(items, tab) {
  return [...items].sort((leftItem, rightItem) => compareReviewInboxItems(
    leftItem,
    rightItem,
    tab,
  ));
}

/**
 * @param {ReviewDetailsInboxItem[]} items
 * @param {'new-reviews' | 'all-reviews'} tab
 * @returns {ReviewDetailsInboxItem[]}
 */
export function filterReviewInboxItems(items, tab) {
  const filtered = tab === 'new-reviews'
    ? items.filter((item) => item.unseenCount > 0)
    : items.filter((item) => item.totalCount > 0);

  return sortReviewInboxItems(filtered, tab);
}

/**
 * Returns session inbox rows in the provided key order without re-sorting.
 *
 * @param {ReviewDetailsInboxItem[]} items
 * @param {string[]} sessionItemOrder
 * @returns {ReviewDetailsInboxItem[]}
 */
export function orderReviewInboxItemsBySessionKeys(items, sessionItemOrder) {
  const itemsByKey = new Map(
    items.map((item) => [buildReviewInboxItemKey(item), item]),
  );

  return sessionItemOrder
    .map((itemKey) => itemsByKey.get(itemKey))
    .filter((item) => item !== undefined);
}

/**
 * Keeps inbox rows visible for the current New Reviews session even after unseen
 * counts drop to zero. Order follows sessionItemOrder; tab is retained for callers.
 *
 * @param {ReviewDetailsInboxItem[]} items
 * @param {string[]} sessionItemOrder
 * @param {'new-reviews' | 'all-reviews'} tab
 * @returns {ReviewDetailsInboxItem[]}
 */
export function filterReviewInboxItemsBySessionKeys(items, sessionItemOrder, _tab) {
  return orderReviewInboxItemsBySessionKeys(items, sessionItemOrder);
}
